import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const WhatIsReplica: React.FC = () => {
    const { t } = useTranslation('marketing');

    const isItems = ['is_1', 'is_2', 'is_3', 'is_4'];
    const isNotItems = ['is_not_1', 'is_not_2', 'is_not_3', 'is_not_4'];

    return (
        <section className="relative py-24 md:py-32 overflow-hidden" id="what-is-replica">
            {/* Static separator line */}
            <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-hig-accent/20 to-transparent" />

            <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#00E5FF]/20 to-transparent" />

            <div className="container-hig">
                <div className="max-w-4xl mx-auto">
                    {/* Section header */}
                    <motion.div
                        className="text-center mb-14"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-80px' }}
                        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                    >
                        <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-white mb-4 tracking-tight">
                            {t('what_is_replica.title')}
                        </h2>
                        <p className="text-base md:text-lg text-white/40 max-w-2xl mx-auto">
                            {t('what_is_replica.subtitle')}
                        </p>
                    </motion.div>

                    {/* Comparison grid */}
                    <div className="grid md:grid-cols-2 gap-5">
                        {/* What a Replica IS */}
                        <motion.div
                            className="relative group card-surface p-7 md:p-8"
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true, margin: '-60px' }}
                            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                            whileHover={{ borderColor: 'rgba(34,197,94,0.2)', transition: { duration: 0.3 } }}
                        >
                            {/* Left accent line */}
                            <motion.div
                                className="absolute left-0 top-6 bottom-6 w-[2px] rounded-full"
                                initial={{ scaleY: 0 }}
                                whileInView={{ scaleY: 1 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.8, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
                                style={{ transformOrigin: 'top', background: 'linear-gradient(180deg, rgba(34,197,94,0.5), rgba(34,197,94,0.1))' }}
                            />
                            <h3 className="text-base font-semibold text-white mb-5 flex items-center gap-2.5">
                                <CheckCircle className="w-4 h-4 text-green-400/80" />
                                {t('what_is_replica.is_title')}
                            </h3>
                            <ul className="space-y-3.5">
                                {isItems.map((key, i) => (
                                    <motion.li
                                        key={key}
                                        className="flex items-start gap-3"
                                        initial={{ opacity: 0, x: -15 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.4, delay: 0.1 + i * 0.08 }}
                                    >
                                        <span className="text-green-400/60 mt-1 text-xs">●</span>
                                        <span className="text-sm text-white/50 leading-relaxed">
                                            {t(`what_is_replica.${key}`)}
                                        </span>
                                    </motion.li>
                                ))}
                            </ul>
                        </motion.div>

                        {/* What a Replica is NOT */}
                        <motion.div
                            className="relative group card-surface p-7 md:p-8"
                            initial={{ opacity: 0, x: 30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true, margin: '-60px' }}
                            transition={{ duration: 0.6, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
                            whileHover={{ borderColor: 'rgba(239,68,68,0.2)', transition: { duration: 0.3 } }}
                        >
                            <motion.div
                                className="absolute left-0 top-6 bottom-6 w-[2px] rounded-full"
                                initial={{ scaleY: 0 }}
                                whileInView={{ scaleY: 1 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.8, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
                                style={{ transformOrigin: 'top', background: 'linear-gradient(180deg, rgba(239,68,68,0.5), rgba(239,68,68,0.1))' }}
                            />
                            <h3 className="text-base font-semibold text-white mb-5 flex items-center gap-2.5">
                                <XCircle className="w-4 h-4 text-red-400/80" />
                                {t('what_is_replica.is_not_title')}
                            </h3>
                            <ul className="space-y-3.5">
                                {isNotItems.map((key, i) => (
                                    <motion.li
                                        key={key}
                                        className="flex items-start gap-3"
                                        initial={{ opacity: 0, x: -15 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.4, delay: 0.15 + i * 0.08 }}
                                    >
                                        <span className="text-red-400/60 mt-1 text-xs">●</span>
                                        <span className="text-sm text-white/50 leading-relaxed">
                                            {t(`what_is_replica.${key}`)}
                                        </span>
                                    </motion.li>
                                ))}
                            </ul>
                        </motion.div>
                    </div>

                    {/* Decision Guardian callout */}
                    <motion.div
                        className="mt-8 px-6 py-4 card-surface"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                    >
                        <p className="text-sm text-white/45 leading-relaxed">
                            <span className="text-hig-accent font-medium">Decision Guardian™:</span>{' '}

                            <span className="text-[#00E5FF] font-medium">Decision Guardian™:</span>{' '}
                            {t('what_is_replica.decision_guardian_desc')}
                        </p>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default WhatIsReplica;
